import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Wand2, ExternalLink } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { AdminCursorTable } from "@/components/dashboard/AdminCursorTable";
import { useServerFn } from "@tanstack/react-start";
import { getProgrammaticStats, listProgrammaticPages } from "@/lib/seo-tabs.functions";

export const Route = createFileRoute("/dashboard/admin/seo-programmatic")({
  head: () => ({ meta: [{ title: "Programmatic Pages · Admin" }, { name: "robots", content: "noindex" }] }),
  component: ProgrammaticPage,
});

type Stats = Awaited<ReturnType<typeof getProgrammaticStats>>;
type Row = Awaited<ReturnType<typeof listProgrammaticPages>>["rows"][number];

function ProgrammaticPage() {
  const fetchStats = useServerFn(getProgrammaticStats);
  const fetchList = useServerFn(listProgrammaticPages);
  const [stats, setStats] = useState<Stats | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [indexable, setIndexable] = useState<"all" | "yes" | "no">("all");

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const resetKey = JSON.stringify({ indexable, q: debounced });

  return (
    <AdminPageShell
      title="Programmatic pages"
      description="Auto-generated specialty, city, and intent pages at scale."
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Filter by slug"
      stats={[
        { label: "Total pages", value: stats?.total.toLocaleString() ?? "—" },
        { label: "Indexed", value: stats?.indexed.toLocaleString() ?? "—" },
        { label: "Noindex", value: stats?.noindex.toLocaleString() ?? "—" },
        { label: "Thin (flagged)", value: stats?.thin.toLocaleString() ?? "—" },
      ]}
      primaryAction={{ label: "Generate pages", icon: Wand2 }}
    >
      <div className="mb-3 flex items-center gap-2">
        {(["all", "yes", "no"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setIndexable(s)}
            className={`h-8 px-3 text-[12px] border rounded-md ${
              indexable === s
                ? "border-primary text-primary font-semibold"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "All" : s === "yes" ? "Indexable" : "Noindex"}
          </button>
        ))}
      </div>

      <AdminCursorTable<Row, string>
        resetKey={resetKey}
        columns={[
          { label: "Slug" },
          { label: "Kind" },
          { label: "Providers" },
          { label: "Indexable" },
          { label: "Refreshed" },
          { label: "" },
        ]}
        rowKey={(r) => r.id}
        fetchPage={async (cursor) =>
          fetchList({
            data: { cursor: cursor as string | null, indexable, q: debounced || undefined },
          })
        }
        renderRow={(r) => (
          <>
            <td className="p-3 font-mono text-xs">{r.slug}</td>
            <td className="p-3 text-[12px]">{r.page_type}</td>
            <td className="p-3 text-[12px]">{r.provider_count ?? 0}</td>
            <td className="p-3 text-[12px]">
              <span className={r.is_indexable ? "text-primary" : "text-muted-foreground"}>
                {r.is_indexable ? "yes" : "no"}
              </span>
            </td>
            <td className="p-3 text-[11px] text-muted-foreground whitespace-nowrap">
              {r.last_refreshed_at ? r.last_refreshed_at.slice(0, 16).replace("T", " ") : "—"}
            </td>
            <td className="p-3 text-right">
              <a
                href={`/${r.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-xs px-2 py-1 border border-border hover:bg-muted rounded-md"
              >
                <ExternalLink className="h-3 w-3 mr-1" /> View
              </a>
            </td>
          </>
        )}
        emptyMessage="No programmatic pages yet."
      />
    </AdminPageShell>
  );
}
